/**
 * ProposeEditsCard - staged review cards for the propose_doc_edits tool (Word).
 *
 * When the model wants to make a substantive change (rewording a clause, adding
 * or removing a provision, changing a cap or date), it stages the edits via
 * propose_doc_edits instead of writing directly. Each edit becomes a card row
 * the user works through one at a time: Apply writes it to the document (via
 * editDocText), Dismiss skips it. The card never touches the document itself; it
 * calls back into hooks.js to apply and to report the outcome.
 *
 * Each row shows the change_summary, the section it belongs to, a compact
 * old -> new diff (with context_before / context_after for readability), the
 * reasoning behind an expandable detail, and, for edits that carry alternatives,
 * a radio group to pick which new_text actually applies. Edits that failed
 * propose-time validation (not uniquely findable) are shown disabled with their
 * error so the user can see why they cannot be applied.
 *
 * Styling mirrors UserQuestionCard (the composer frame): neutral border at rest,
 * warm accent only on hover / selection.
 *
 * When every row is resolved (or the user clicks Done), onSubmit is called with
 * { applied: [{ id }], dismissed: [id], failed: [{ id, error }] } so the model
 * gets the review outcome as the tool result (our equivalent of the reference
 * <user_reviewed_edits> block).
 */

import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { getChatRadii, getChatBorders } from '../chatStyles';

// Per-row lifecycle. 'pending' rows still need a decision; the rest are resolved.
const STATUS = {
  PENDING: 'pending',
  APPLYING: 'applying',
  APPLIED: 'applied',
  DISMISSED: 'dismissed',
  FAILED: 'failed',
  INVALID: 'invalid'
};

const isResolved = (status) =>
  status === STATUS.APPLIED ||
  status === STATUS.DISMISSED ||
  status === STATUS.FAILED ||
  status === STATUS.INVALID;

const ProposeEditsCard = ({ request, onApply, onSubmit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentWarm = theme.palette.accentBubble?.warm || '#D4694E';
  const radii = getChatRadii(theme);
  const borders = getChatBorders(theme);
  const okGreen = isDark ? '#6FCF97' : '#2E7D52';
  const errRed = isDark ? '#E5736A' : '#C0392B';

  const edits = useMemo(() => (Array.isArray(request?.edits) ? request.edits : []), [request]);

  // Validity comes from proposeDocEdits: a Set of edit ids that are not findable,
  // each with its error message. Invalid rows are disabled.
  const invalidMap = useMemo(() => {
    const map = new Map();
    (request?.invalid || []).forEach((e) => {
      if (e && e.id != null) map.set(String(e.id), e.error || 'This edit is no longer findable in the document.');
    });
    return map;
  }, [request]);

  // Per-row state: { status, error, choiceIndex } where choiceIndex selects
  // which new_text to apply (0 = primary new_text, 1..n = alternatives).
  const [rows, setRows] = useState(() =>
    edits.map((edit) => {
      const id = String(edit?.id);
      const invalid = invalidMap.has(id);
      return {
        status: invalid ? STATUS.INVALID : STATUS.PENDING,
        error: invalid ? invalidMap.get(id) : null,
        choiceIndex: 0
      };
    })
  );

  const [submitted, setSubmitted] = useState(false);

  // The candidate texts for an edit: the primary new_text first, then any
  // labeled alternatives. Used to render the radio group and to resolve which
  // text Apply should write.
  const candidatesFor = (edit) => {
    const primaryLabel = edit?.new_text_label || 'Recommended';
    const list = [{ label: primaryLabel, text: edit?.new_text ?? '' }];
    (Array.isArray(edit?.alternatives) ? edit.alternatives : []).forEach((alt) => {
      if (alt && typeof alt.text === 'string') list.push({ label: alt.label || 'Alternative', text: alt.text });
    });
    return list;
  };

  const setRow = (index, patch) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  // Build the review outcome from the current row states. Applied rows carry
  // their id (plus the chosen text label for traceability); dismissed rows are
  // bare ids; failed rows carry their error.
  const buildOutcome = (rowState) => {
    const applied = [];
    const dismissed = [];
    const failed = [];
    edits.forEach((edit, i) => {
      const id = edit?.id;
      const r = rowState[i];
      if (!r) return;
      if (r.status === STATUS.APPLIED) applied.push({ id });
      else if (r.status === STATUS.DISMISSED) dismissed.push(id);
      else if (r.status === STATUS.FAILED || r.status === STATUS.INVALID) {
        failed.push({ id, error: r.error || 'edit could not be applied' });
      }
    });
    return { applied, dismissed, failed };
  };

  const allResolved = (rowState) => rowState.every((r) => isResolved(r.status));

  const finishIfDone = (rowState) => {
    if (!submitted && allResolved(rowState)) {
      setSubmitted(true);
      onSubmit(buildOutcome(rowState));
    }
  };

  const applyRow = async (index) => {
    const edit = edits[index];
    if (!edit) return;
    const choice = candidatesFor(edit)[rows[index].choiceIndex] || candidatesFor(edit)[0];

    setRow(index, { status: STATUS.APPLYING, error: null });
    try {
      // onApply is the hooks.js bridge call: editDocText({ old_text, new_text }).
      // It resolves with the bridge result so we can surface a not-found failure.
      const res = await onApply({ old_text: edit.old_text, new_text: choice.text });
      const applied = res && (res.success === true || res.replacements > 0) && !res.notFound;
      if (applied) {
        setRows((prev) => {
          const next = prev.map((r, i) => (i === index ? { ...r, status: STATUS.APPLIED, error: null } : r));
          finishIfDone(next);
          return next;
        });
      } else {
        const error = (res && res.error) || 'old_text was no longer findable when applying.';
        setRows((prev) => {
          const next = prev.map((r, i) => (i === index ? { ...r, status: STATUS.FAILED, error } : r));
          finishIfDone(next);
          return next;
        });
      }
    } catch (err) {
      const error = err?.message || 'Failed to apply this edit.';
      setRows((prev) => {
        const next = prev.map((r, i) => (i === index ? { ...r, status: STATUS.FAILED, error } : r));
        finishIfDone(next);
        return next;
      });
    }
  };

  const dismissRow = (index) => {
    setRows((prev) => {
      const next = prev.map((r, i) => (i === index ? { ...r, status: STATUS.DISMISSED, error: null } : r));
      finishIfDone(next);
      return next;
    });
  };

  const applyAll = () => {
    rows.forEach((r, i) => {
      if (r.status === STATUS.PENDING) applyRow(i);
    });
  };

  const dismissAll = () => {
    setRows((prev) => {
      const next = prev.map((r) => (r.status === STATUS.PENDING ? { ...r, status: STATUS.DISMISSED, error: null } : r));
      finishIfDone(next);
      return next;
    });
  };

  const submitNow = () => {
    // Treat any still-pending rows as dismissed, then report.
    setRows((prev) => {
      const next = prev.map((r) => (r.status === STATUS.PENDING ? { ...r, status: STATUS.DISMISSED, error: null } : r));
      if (!submitted) {
        setSubmitted(true);
        onSubmit(buildOutcome(next));
      }
      return next;
    });
  };

  const [expanded, setExpanded] = useState({});
  const toggleExpanded = (index) => setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));

  if (edits.length === 0) return null;

  const pendingCount = rows.filter((r) => r.status === STATUS.PENDING).length;

  // Small status pill shown once a row is resolved.
  const statusPill = (status) => {
    if (status === STATUS.APPLIED) return { label: 'Applied', color: okGreen };
    if (status === STATUS.DISMISSED) return { label: 'Dismissed', color: theme.palette.text.secondary };
    if (status === STATUS.FAILED) return { label: 'Failed', color: errRed };
    if (status === STATUS.INVALID) return { label: 'Not findable', color: errRed };
    if (status === STATUS.APPLYING) return { label: 'Applying...', color: theme.palette.text.secondary };
    return null;
  };

  const labelChipSx = {
    px: 0.9,
    py: 0.2,
    borderRadius: radii.chip,
    backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.07),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  };

  const monoSx = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: theme.typography.pxToRem(11.5),
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  };

  const ghostBtnSx = (tone) => ({
    textTransform: 'none',
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
    borderRadius: radii.pill,
    px: 1.5,
    py: 0.4,
    minWidth: 0,
    color: tone === 'apply' ? accentWarm : theme.palette.text.secondary,
    border: `${borders.width}px solid ${tone === 'apply' ? alpha(accentWarm, isDark ? 0.5 : 0.45) : borders.restingColor}`,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: tone === 'apply'
        ? alpha(accentWarm, isDark ? 0.1 : 0.06)
        : alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
      borderColor: tone === 'apply' ? alpha(accentWarm, isDark ? 0.6 : 0.5) : borders.emphasisColor
    }
  });

  return (
    <Box
      sx={{
        mx: 1.5,
        mb: 1,
        p: 1.75,
        borderRadius: radii.card,
        border: borders.resting,
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      {/* Header: count of proposed edits + batch actions. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.pxToRem(13),
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {edits.length} proposed edit{edits.length === 1 ? '' : 's'}
        </Typography>
        {pendingCount > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button disableElevation onClick={applyAll} sx={ghostBtnSx('apply')}>
              Apply all
            </Button>
            <Button disableElevation onClick={dismissAll} sx={ghostBtnSx('dismiss')}>
              Dismiss all
            </Button>
          </Box>
        )}
      </Box>

      {edits.map((edit, index) => {
        const row = rows[index] || { status: STATUS.PENDING, choiceIndex: 0 };
        const candidates = candidatesFor(edit);
        const hasAlternatives = candidates.length > 1;
        const pill = statusPill(row.status);
        const disabled = row.status === STATUS.INVALID;
        const resolved = isResolved(row.status);
        const isExpanded = !!expanded[index];
        const activeText = (candidates[row.choiceIndex] || candidates[0]).text;

        return (
          <Box
            key={edit?.id || `edit-${index}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.9,
              p: 1.25,
              borderRadius: radii.nested,
              border: borders.resting,
              opacity: disabled || (resolved && row.status !== STATUS.FAILED && row.status !== STATUS.INVALID) ? 0.72 : 1
            }}
          >
            {/* Row header: section chip + change summary + status pill. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, flexWrap: 'wrap' }}>
              {edit?.section && <Box sx={labelChipSx}>{edit.section}</Box>}
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.pxToRem(13),
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  lineHeight: 1.35,
                  minWidth: 0
                }}
              >
                {edit?.change_summary || 'Proposed edit'}
              </Typography>
              {pill && (
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.pxToRem(11),
                    fontWeight: 700,
                    color: pill.color,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {pill.label}
                </Typography>
              )}
            </Box>

            {/* Compact old -> new diff, wrapped in context for readability. */}
            <Box
              sx={{
                borderRadius: radii.nested,
                border: borders.resting,
                overflow: 'hidden'
              }}
            >
              <Box sx={{ px: 1.1, py: 0.7, backgroundColor: alpha(errRed, isDark ? 0.12 : 0.07) }}>
                <Typography sx={{ ...monoSx, color: theme.palette.text.primary }}>
                  {edit?.context_before && (
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>{edit.context_before}</Box>
                  )}
                  <Box component="span" sx={{ color: errRed, textDecoration: 'line-through' }}>{edit?.old_text}</Box>
                  {edit?.context_after && (
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>{edit.context_after}</Box>
                  )}
                </Typography>
              </Box>
              <Box sx={{ px: 1.1, py: 0.7, backgroundColor: alpha(okGreen, isDark ? 0.12 : 0.07) }}>
                <Typography sx={{ ...monoSx, color: theme.palette.text.primary }}>
                  {edit?.context_before && (
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>{edit.context_before}</Box>
                  )}
                  <Box component="span" sx={{ color: okGreen, fontWeight: 600 }}>{activeText}</Box>
                  {edit?.context_after && (
                    <Box component="span" sx={{ color: theme.palette.text.secondary }}>{edit.context_after}</Box>
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Alternatives: a radio group choosing which new_text applies. */}
            {hasAlternatives && !resolved && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {candidates.map((cand, ci) => {
                  const selected = row.choiceIndex === ci;
                  return (
                    <Box
                      key={`${index}-${ci}-${cand.label}`}
                      component="button"
                      type="button"
                      onClick={() => setRow(index, { choiceIndex: ci })}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        textAlign: 'left',
                        appearance: 'none',
                        cursor: 'pointer',
                        textTransform: 'none',
                        px: 1.1,
                        py: 0.7,
                        borderRadius: radii.nested,
                        border: `${borders.width}px solid ${selected ? alpha(accentWarm, isDark ? 0.5 : 0.45) : borders.restingColor}`,
                        backgroundColor: selected ? alpha(accentWarm, isDark ? 0.1 : 0.06) : 'transparent',
                        color: theme.palette.text.primary,
                        '&:hover': {
                          borderColor: alpha(accentWarm, isDark ? 0.45 : 0.4)
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: radii.circle,
                          flexShrink: 0,
                          border: `${borders.emphasisWidth}px solid ${selected ? accentWarm : borders.emphasisColor}`,
                          backgroundColor: selected ? accentWarm : 'transparent'
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.pxToRem(12),
                          fontWeight: selected ? 600 : 500,
                          color: 'inherit'
                        }}
                      >
                        {cand.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Reasoning behind an expandable detail. */}
            {edit?.reasoning && (
              <Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleExpanded(index)}
                  sx={{
                    appearance: 'none',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                    color: theme.palette.text.secondary,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: theme.typography.pxToRem(11.5),
                    fontWeight: 600
                  }}
                >
                  {isExpanded ? 'Hide reasoning' : 'Why this change'}
                </Box>
                {isExpanded && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.pxToRem(12),
                      lineHeight: 1.45,
                      color: theme.palette.text.secondary
                    }}
                  >
                    {edit.reasoning}
                  </Typography>
                )}
              </Box>
            )}

            {/* Failure / invalid error message. */}
            {(row.status === STATUS.FAILED || row.status === STATUS.INVALID) && row.error && (
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.pxToRem(11.5),
                  lineHeight: 1.4,
                  color: errRed
                }}
              >
                {row.error}
              </Typography>
            )}

            {/* Per-row actions (only while pending). */}
            {row.status === STATUS.PENDING && (
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Button disableElevation onClick={() => applyRow(index)} sx={ghostBtnSx('apply')}>
                  Apply
                </Button>
                <Button disableElevation onClick={() => dismissRow(index)} sx={ghostBtnSx('dismiss')}>
                  Dismiss
                </Button>
              </Box>
            )}
          </Box>
        );
      })}

      {/* Done / Submit: lets the user close the loop without resolving every row
          individually (still-pending rows are reported as dismissed). */}
      {!submitted && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disableElevation
            onClick={submitNow}
            sx={{
              textTransform: 'none',
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: theme.typography.pxToRem(12.5),
              borderRadius: radii.pill,
              px: 2,
              py: 0.6
            }}
          >
            {pendingCount > 0 ? 'Done reviewing' : 'Send review'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProposeEditsCard;
